import React, {
  createContext, useCallback, useMemo, useState,
} from 'react';
import { captureException } from 'utils/errors';
import { STEPS } from '../constants';

export const StepsContext = createContext();

const getStepById = (id) => Object.keys(STEPS)[id - 1];

function StepsProvider({ children }) {
  const [activeStep, setActiveStep] = useState(STEPS.GENERAL_INFO.id);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);

  const onNextStep = useCallback((form) => {
    validate(form).then(() => {
      if (activeStep === Object.keys(STEPS).length) {
        return;
      }

      const newCompletedSteps = Array.from(new Set([...completedSteps, getStepById(activeStep)]));
      setCompletedSteps(newCompletedSteps);
      setActiveStep((step) => step + 1);
    })
      .catch((e) => {
        captureException(e);
      });
  }, [activeStep, completedSteps]);

  const onPrevStep = useCallback(() => setActiveStep((step) => Math.max((step - 1), 1)), []);

  function isActiveStep(step) {
    return step === activeStep;
  }

  const validate = (form, stepId = activeStep) => {
    const stepConfig = Object.entries(STEPS)[stepId - 1][1];
    const fields = Array.isArray(stepConfig?.fields) ? [...stepConfig.fields] : [];
    if (stepId === STEPS.PAYMENT_INFO.id) {
      const meta = form.getFieldValue('payment_method')?.meta;
      if (meta) {
        const metaFields = meta ? Object.entries(meta?.fields) : [];
        metaFields?.forEach(([name]) => fields.push(['customPaymentMeta', name]));
      }
    }
    return form
      .validateFields(fields, { validateOnly: false });
  };

  const validateIntermediateSteps = useCallback(async (form, targetStep) => {
    // When jumping forward, validate all steps from current to target (inclusive of current, exclusive of target)
    // This ensures all intermediate steps are validated
    const stepsToValidate = [];
    for (let stepId = activeStep; stepId < targetStep; stepId++) {
      stepsToValidate.push(stepId);
    }
    
    // Validate all intermediate steps
    const newCompletedSteps = [...completedSteps];
    for (const stepId of stepsToValidate) {
      try {
        await validate(form, stepId);
        // Mark step as completed if validation passes
        const stepKey = getStepById(stepId);
        if (!newCompletedSteps.includes(stepKey)) {
          newCompletedSteps.push(stepKey);
        }
      } catch (error) {
        // If validation fails, throw error to prevent navigation
        throw error;
      }
    }
    
    // Update completed steps only if all validations passed
    setCompletedSteps(Array.from(new Set(newCompletedSteps)));
  }, [activeStep, completedSteps]);

  const onStepClick = useCallback(async (form, targetStep) => {
    if (targetStep === activeStep) {
      return; // Already on this step
    }

    setLoading(true);
    try {
      // If jumping forward, validate all intermediate steps
      if (targetStep > activeStep) {
        await validateIntermediateSteps(form, targetStep);
      }
      // If jumping backward, no validation needed - allow free navigation
      setActiveStep(targetStep);
    } catch (error) {
      captureException(error);
      // Error is already shown by form validation, don't navigate
    } finally {
      setLoading(false);
    }
  }, [activeStep, validateIntermediateSteps]);

  const isCompleted = (step) => completedSteps.includes(getStepById(step));

  const contextValue = useMemo(() => ({
    activeStep,
    setActiveStep,
    onNextStep,
    onPrevStep,
    onStepClick,
    loading,
    setLoading,
    isActiveStep,
    validate,
    isCompleted,
    locked,
    lock() { setLocked(true); },
    unlock() { setLocked(false); },
  }), [activeStep, onNextStep, onPrevStep, onStepClick, loading, completedSteps, locked]);

  return (
    <StepsContext.Provider value={contextValue}>
      {children}
    </StepsContext.Provider>
  );
}

export default StepsProvider;
